import type { FindingsScope } from "@iamu/shared";
import { RotateCcw } from "lucide-react";
import { useId, useState } from "react";
import { lensColorMap } from "@/components/finding-badges";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { ModeToggle } from "./findings-scope-bar";
import { PeriodOptions } from "./period-options";
import {
  ALL,
  COMMITTED_HELP,
  facetOptions,
  LENS_OPTIONS,
  type LensValue,
  MODE_HELP,
  periodPhrase,
  SEVERITY_OPTIONS,
  type SeverityValue,
  type ScopeProps,
  useScopeBuilds,
  useScopeEntities,
  withPeriod,
} from "./scope-hooks";
import { environmentLabel, isScoped } from "./scope-url-state";

export function MobileScope({ projectId, scope, setScope }: ScopeProps) {
  const [open, setOpen] = useState(false);
  const committedId = useId();
  const { personas, capabilities } = useScopeEntities(projectId);
  const { builds, selectedLabel, environments, branches, hasUncommitted } =
    useScopeBuilds(projectId, scope.period);
  const set = (patch: Partial<FindingsScope>) =>
    setScope({ ...scope, ...patch });

  const persona = personas.find((p) => p.id === scope.personaId)?.name;
  const capability = capabilities.find(
    (c) => c.id === scope.capabilityId,
  )?.name;
  const severity = SEVERITY_OPTIONS.find(
    (s) => s.value === scope.severities?.[0],
  )?.label;
  const lens = scope.lens ? lensColorMap[scope.lens].label : null;

  const parts: Array<{ key: string; text: string; emphasis?: boolean }> = [];
  if (scope.period) {
    parts.push({ key: "f", text: "Findings" });
    parts.push({
      key: "mode",
      text: scope.periodMode === "new" ? "new" : "still happening",
      emphasis: true,
    });
  } else {
    parts.push({ key: "f", text: "All findings," });
  }
  parts.push({
    key: "period",
    text: periodPhrase(scope.period, selectedLabel),
    emphasis: true,
  });
  if (persona)
    parts.push(
      { key: "pf", text: "for" },
      { key: "p", text: persona, emphasis: true },
    );
  if (capability)
    parts.push(
      { key: "cf", text: "in" },
      { key: "c", text: capability, emphasis: true },
    );
  if (severity) parts.push({ key: "s", text: `· ${severity}`, emphasis: true });
  if (lens) parts.push({ key: "l", text: `· ${lens} lens`, emphasis: true });
  if (scope.environment)
    parts.push(
      { key: "ef", text: "on" },
      { key: "e", text: environmentLabel(scope.environment), emphasis: true },
    );
  if (scope.branch)
    parts.push(
      { key: "bf", text: scope.environment ? "· branch" : "on branch" },
      { key: "b", text: scope.branch, emphasis: true },
    );
  if (scope.committedOnly)
    parts.push({ key: "w", text: "· committed runs only", emphasis: true });
  const sentence = parts.map((p) => p.text).join(" ");

  return (
    <div className="md:hidden">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Change scope. Showing: ${sentence}`}
          className="flex-1 rounded-sm py-1 text-left text-base leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {parts.map((p) => (
            <span
              key={p.key}
              className={cn(
                p.emphasis &&
                  "font-medium underline decoration-foreground/40 decoration-dashed underline-offset-4",
              )}
            >
              {p.text}{" "}
            </span>
          ))}
        </button>
        {isScoped(scope) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setScope({})}
            aria-label="Reset scope"
            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="size-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-4 sm:p-6"
        >
          <SheetHeader className="border-b p-0 pb-3">
            <SheetTitle className="text-base font-semibold">
              Scope of findings
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg border">
              <PeriodOptions
                period={scope.period}
                builds={builds}
                onChange={(p) => setScope(withPeriod(scope, p))}
              />
            </div>
            {scope.period && (
              <div className="space-y-1.5">
                <ModeToggle
                  value={scope.periodMode ?? "seen"}
                  onChange={(m) =>
                    set({ periodMode: m === "new" ? "new" : undefined })
                  }
                />
                <p className="text-xs text-muted-foreground">{MODE_HELP}</p>
              </div>
            )}
            {personas.length > 0 && (
              <ScopeSelect
                label="Persona"
                value={scope.personaId}
                allLabel="All personas"
                options={personas.map((p) => ({ value: p.id, label: p.name }))}
                onChange={(v) => set({ personaId: v })}
              />
            )}
            {capabilities.length > 0 && (
              <ScopeSelect
                label="Capability"
                value={scope.capabilityId}
                allLabel="All capabilities"
                options={capabilities.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                onChange={(v) =>
                  set({
                    capabilityId: v,
                    capabilityScope: v ? scope.capabilityScope : undefined,
                  })
                }
              />
            )}
            <ScopeSelect
              label="Severity"
              value={scope.severities?.[0]}
              allLabel="Any severity"
              options={SEVERITY_OPTIONS}
              onChange={(v) =>
                set({ severities: v ? [v as SeverityValue] : undefined })
              }
            />
            <ScopeSelect
              label="Lens"
              value={scope.lens}
              allLabel="Every lens"
              options={LENS_OPTIONS}
              onChange={(v) => set({ lens: v as LensValue | undefined })}
            />
            {(environments.length > 1 || scope.environment) && (
              <ScopeSelect
                label="Environment"
                value={scope.environment}
                allLabel="All environments"
                options={facetOptions(
                  environments,
                  scope.environment,
                  environmentLabel,
                )}
                onChange={(v) => set({ environment: v })}
              />
            )}
            {(branches.length > 1 || scope.branch) && (
              <ScopeSelect
                label="Branch"
                value={scope.branch}
                allLabel="All branches"
                options={facetOptions(branches, scope.branch)}
                onChange={(v) => set({ branch: v })}
              />
            )}
            {(hasUncommitted || scope.committedOnly) && (
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor={committedId}>Hide uncommitted runs</Label>
                  <p className="text-xs text-muted-foreground">
                    {COMMITTED_HELP}
                  </p>
                </div>
                <Switch
                  id={committedId}
                  checked={Boolean(scope.committedOnly)}
                  onCheckedChange={(on) =>
                    set({ committedOnly: on ? true : undefined })
                  }
                />
              </div>
            )}
          </div>
          <SheetFooter className="flex-row gap-2 border-t p-0 pt-2">
            {isScoped(scope) && (
              <Button
                variant="outline"
                onClick={() => setScope({})}
                className="flex-1"
              >
                Reset scope
              </Button>
            )}
            <Button className="flex-1" onClick={() => setOpen(false)}>
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ScopeSelect(props: {
  label: string;
  value: string | undefined;
  allLabel: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string | undefined) => void;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{props.label}</Label>
      <Select
        value={props.value ?? ALL}
        onValueChange={(v) => props.onChange(v === ALL ? undefined : v)}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{props.allLabel}</SelectItem>
          {props.options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
